import { madcapCustomAttrName } from '@common/flare/schema/custom-attributes-schema-plugin';
import { attrToFlareValue } from '@common/flare/util/prosemirror-flare-xml';
import { mergeStyles, removeEmptyStyles } from '@common/html/util/style';
import { TrackedChangesPluginState } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { applyChangeToNodeAttrs } from '@common/prosemirror/tracked-changes/copy';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { getTrackedChangeNodeAttrs, makeTrackedChange } from '@common/prosemirror/tracked-changes/tracked-changes';
import { isEqual } from 'lodash';
import { AttributeSpec, Attrs, Node } from 'prosemirror-model';
import { Transform } from 'prosemirror-transform';

/**
 * Options for updating a node's attributes.
 */
export interface UpdateFlareNodeAttrsOptions {
  /** Whether the style attribute should be merged with the existing style attribute. Defaults to true. */
  mergeStyles?: boolean;
}

function diffObjects(oldObj: Object, newObj: Object): Dictionary<{ oldValue: any, newValue: any }> {
  const keys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

  const diff = {};

  for (const key of keys) {
    if (key === 'style') {
      // Remove empty styles for proper comparision
      oldObj[key] = removeEmptyStyles(oldObj[key]);
      newObj[key] = removeEmptyStyles(newObj[key]);
    }

    if (!isEqual(oldObj[key], newObj[key])) {
      diff[key] = {
        oldValue: oldObj[key],
        newValue: newObj[key]
      };
    }
  }

  return diff;
}

/**
 * Returns the tracked change attrs from setting an attr value on a node.
 * @param node The node to make the tracked change attrs for.
 * @param trackedChangeNodeAttrs The existing tracked change attrs on the node.
 * @param key The attr's key.
 * @param value The old and new values of the attr.
 * @param trackedChangesState The tracked changes plugin state.
 * @returns The new tracked change attrs for the node.
 */
function trackFlareNodeAttrChange(node: Node, trackedChangeNodeAttrs: TrackedChangeNodeAttrs, key: string, value: { oldValue: any, newValue: any }, trackedChangesState: TrackedChangesPluginState): TrackedChangeNodeAttrs {
  // Get the xml value
  const flareAttr = attrToFlareValue(key, value.oldValue, node.type.spec.attrs, node.type.name, node);
  let changeValue = flareAttr?.xml;
  if (flareAttr?.xml) {
    // When the attribute is a dictionary, throw an exception
    throw new Error(`Attribute value not a string. Node: ${node.type.name}, Attribute: ${key}, Value: ${changeValue}`);
  }

  if (flareAttr?.newValue) {
    changeValue = flareAttr?.newValue;
  } else {
    changeValue = value.oldValue;
  }

  // Otherwise process the original value
  if (typeof changeValue !== 'undefined' && changeValue !== null) {
    if (typeof changeValue !== 'string' && typeof changeValue !== 'number') {
      throw new Error(`Attribute value not a string. Node: ${node.type.name}, Attribute: ${key}, Value: ${changeValue}`);
    }

    if (changeValue === '') {
      return trackedChangeNodeAttrs;
    }
  }

  const attrSpec: AttributeSpec = node.type.spec.attrs[key];
  const exportedName: string = attrSpec?.exportedName;
  return applyChangeToNodeAttrs(trackedChangeNodeAttrs, makeTrackedChange(ChangeType.Attributes, trackedChangesState.user, { changeAttribute: exportedName ?? key, changeValue: changeValue }));
}

/**
 * Upgrades MadCap:custom attrs that have been moved to a top level attr.
 * This is needed for when the schema is upgraded such that an attr is no longer stored in MadCap:custom but instead is its own top level attr.
 * Upgrades by moving the attr from MadCap:custom to the top level attr so that a subsequent diff by updateFlareNodeAttrs will detect the change correctly.
 * @param oldAttrs The original attributes.
 * @param newAttrs The new attributes being set.
 * @returns The upgraded attributes.
 */
export function upgradeCustomAttrs(oldAttrs: Attrs, newAttrs: Attrs): Attrs {
  let upgradedAttrs: Dictionary;

  // If the old attrs have MadCap:custom then we need to remove any attributes that are being set on the top level
  if (oldAttrs?.[madcapCustomAttrName]) {
    // Loop through the new attrs
    for (const [key, _] of Object.entries(newAttrs)) {
      // If the old MadCap:custom has this attr then remove it from MadCap:custom
      if (oldAttrs[madcapCustomAttrName]?.hasOwnProperty(key)) {
        // Initialize upgradedAttrs if needed. Do this here so that we are only creating new objects when needed.
        upgradedAttrs = upgradedAttrs ?? {
          ...oldAttrs,
          [madcapCustomAttrName]: { ...oldAttrs[madcapCustomAttrName] }
        };

        // Move the attr from MadCap:custom to the top level. If for some reason the top level attr is already set then do not overwrite it.
        upgradedAttrs[key] = upgradedAttrs[key] ?? upgradedAttrs[madcapCustomAttrName][key];

        // Remove the attr from MadCap:custom
        delete upgradedAttrs[madcapCustomAttrName][key];
      }
    }

    // If MadCap:custom is now empty then remove it
    if (upgradedAttrs?.[madcapCustomAttrName] && Object.keys(upgradedAttrs[madcapCustomAttrName]).length === 0) {
      delete upgradedAttrs[madcapCustomAttrName];
    }
  }

  return upgradedAttrs ?? oldAttrs;
}

/**
 * Sets the attributes of a node by adding/setting the given attrs to the node's existing attributes.
 * Is aware of special Flare attributes such as style and MadCap:custom.
 * By default the style attribute is merged with the existing style attribute.
 * @param tr The transform to update the node with.
 * @param node The node to update.
 * @param pos The position of the node in the document.
 * @param attrs The attributes to add/set on the node.
 * @param trackedChangesState The tracked changes plugin state.
 * @param options special rules on how to merge some things.
 * @returns The transform with the node updated.
 */
export function updateFlareNodeAttrs(tr: Transform, node: Node, pos: number, attrs: Attrs, trackedChangesState: TrackedChangesPluginState, options?: UpdateFlareNodeAttrsOptions): Transform {
  const oldAttrs = upgradeCustomAttrs(node.attrs, attrs);
  let newAttrs = mergeFlareNodeAttrs(oldAttrs, attrs, options);

  // If tracked changes are enabled
  if (trackedChangesState?.enabled) {
    // Grab the attributes that were changed
    const diff = diffObjects(oldAttrs, newAttrs);

    // Grab the existing tracked changes on the node
    let trackedChangeNodeAttrs = getTrackedChangeNodeAttrs(trackedChangesState.changeMarkType, node);

    // For each attribute that was changed, add an Attributes tracked change
    for (const [key, value] of Object.entries(diff)) {
      // Skip when the attribute wasn't initialized and the new value doesn't assign
      if (value.oldValue == null && value.newValue == null) {
        continue;
      }

      // If this is a MadCap:custom attr then loop through each of its attrs and apply the changes like they were top level attrs
      if (key === madcapCustomAttrName) {
        const customDiff = diffObjects(value.oldValue ?? {}, value.newValue ?? {});
        for (const [customKey, customValue] of Object.entries(customDiff)) {
          trackedChangeNodeAttrs = trackFlareNodeAttrChange(node, trackedChangeNodeAttrs, customKey, customValue, trackedChangesState);
        }
      } else {
        trackedChangeNodeAttrs = trackFlareNodeAttrChange(node, trackedChangeNodeAttrs, key, value, trackedChangesState);
      }
    }

    newAttrs = {
      ...newAttrs,
      ...trackedChangeNodeAttrs
    };
  }

  return tr.setNodeMarkup(pos, null, newAttrs);
}

/**
 * Merges two attribute dictionaries together.
 * By default the style attribute is merged with the existing style attribute.
 * @param attrs1 The attrs the node already has.
 * @param attrs2 The attrs to update a node with.
 * @param options special rules on how to merge some things.
 * @returns The merged attributes.
 */
export function mergeFlareNodeAttrs(attrs1: Attrs, attrs2: Attrs, options?: UpdateFlareNodeAttrsOptions): Attrs {
  let newAttrs = {
    ...attrs1,
    ...attrs2
  };

  if (options?.mergeStyles !== false) {
    newAttrs.style = mergeStyles(attrs1?.style, attrs2?.style);
  }

  return newAttrs;
}

/**
 * Returns the attr's value if it exists on the node falling back to getting the value from the MadCap:custom attribute.
 * This is for backwards compatibility when we move an attribute from the MadCap:custom attribute into its own top level attribute.
 * @param node The node to get the attr value from.
 * @param attrName The name of the attr to get.
 * @returns The attr value
 */
export function getFlareAttr<T>(nodeOrAttrs: Node | Attrs, attrName: string): T {
  const attrs = nodeOrAttrs instanceof Node ? nodeOrAttrs.attrs : nodeOrAttrs;
  return attrs[attrName] ?? attrs[madcapCustomAttrName]?.[attrName];
}
